import { ColumnDef } from '@tanstack/react-table'
import { InventoryItem } from '../interfaces/inventory'
import { ExpirationBadge } from '../components/ExpirationBadge'

// Note: This will be injected by the component that uses these columns
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let _setSelectedProductId: ((_productId: number) => void) | null = null

export const injectProductSelector = (
  selector: (_productId: number) => void
) => {
  _setSelectedProductId = selector
}

export const columns: ColumnDef<InventoryItem>[] = [
  {
    accessorKey: 'productName',
    header: 'Nombre',
    cell: ({ row }) => {
      // Support both new API format and legacy format
      const name = row.original?.productName || row.original?.name || ''

      return (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
            <div className="flex h-full w-full items-center justify-center text-sm font-medium text-gray-500">
              {name ? name.charAt(0).toUpperCase() : 'P'}
            </div>
          </div>
          <span className="text-gray-900">{name}</span>
        </div>
      )
    },
    enableHiding: false,
  },
  {
    accessorKey: 'quantity',
    header: 'Cantidad',
    cell: ({ row }) => {
      const item = row.original
      // Support both new API format (with entryQuantity/availableQuantity) and legacy format
      let totalQuantity = 0

      if (typeof item?.quantity === 'number') {
        // Legacy format
        totalQuantity = item.quantity
      } else if (item?.quantity && typeof item.quantity === 'object') {
        // New API format
        totalQuantity = item.quantity.entryQuantity || 0
      }

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">{totalQuantity}</span>
        </div>
      )
    },
    enableHiding: false,
  },
  {
    accessorKey: 'containers',
    header: 'Contenedores',
    cell: ({ row }) => {
      const item = row.original
      let containerCount = 0

      // Support both new API format (array of container objects) and legacy format (number)
      if (Array.isArray(item?.containers)) {
        // New API format - just count the containers
        containerCount = item.containers.length
      } else if (typeof item?.containers === 'number') {
        // Legacy format - use the number directly
        containerCount = item.containers
      }

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">{containerCount}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    accessorKey: 'suppliers',
    header: 'Proveedores',
    cell: ({ row }) => {
      const item = row.original
      let displayText = ''

      // Support both new API format (array of supplier objects) and legacy format (array of strings)
      if (Array.isArray(item?.suppliers)) {
        let supplierNames: string[] = []

        // New API format - suppliers are always objects with name property
        if (item.suppliers.length > 0) {
          supplierNames = item.suppliers.map((s) => s.name)
        }

        // Remove duplicates by converting to Set and back to array
        const uniqueSupplierNames = Array.from(new Set(supplierNames))

        const displaySuppliers = uniqueSupplierNames.slice(0, 3)
        displayText = displaySuppliers.join(', ')

        const remainingCount =
          uniqueSupplierNames.length > 3 ? uniqueSupplierNames.length - 3 : 0
        if (remainingCount > 0) {
          displayText += ` +${remainingCount} más`
        }
      }

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">{displayText || '-'}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    accessorKey: 'expirationDate',
    header: 'Vencimiento',
    cell: ({ row }) => {
      const item = row.original

      // Support both new API format (expirations array) and legacy format (expirationDate)
      let expirationDate: string | null = null
      let lotCount = 1

      if (Array.isArray(item?.expirations) && item.expirations.length > 0) {
        // New API format - expirations is array of strings with dates
        const sortedExpirations = [...item.expirations].sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime()
        )
        expirationDate = sortedExpirations[0]
        lotCount = item.expirations.length
      } else if (item?.expirationDate) {
        // Legacy format
        expirationDate = item.expirationDate
        lotCount = item?.lotCount || 1
      }

      if (!expirationDate) {
        return (
          <div className="flex w-full flex-col items-start text-left">
            <span className="text-gray-400">-</span>
          </div>
        )
      }

      const today = new Date()
      const expiry = new Date(expirationDate)
      const diffTime = expiry.getTime() - today.getTime()
      const daysUntilExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      return (
        <div className="flex w-full flex-col items-start text-left">
          <ExpirationBadge count={lotCount} daysUntilExpiry={daysUntilExpiry} />
        </div>
      )
    },
    enableHiding: true,
  },
]
